import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Plus as PlusIcon, Trash2, Receipt as ReceiptIcon, Undo2, Send, Copy, CheckCircle2, Pencil, History, Share2, Mail, Download } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, GhostButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import HistoryDrawer from "@/components/HistoryDrawer";
import InvoiceShareModal from "@/components/InvoiceShareModal";
import { SortableTh, useTableControls, FilterChip } from "@/components/SortableTable";
import { fmtDateTime, fmtDate, istDateKey } from "@/lib/time";
import { caseLabel } from "@/lib/labels";
import FilterPill from "@/components/FilterPill";
import useRowKeyboardNav from "@/lib/useRowKeyboardNav";

function relTime(iso) {
    if (!iso) return null;
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return fmtDate(iso);
}

function ReadReceipt({ ev }) {
    if (!ev) return null;
    const lastEv = ev.last_view || ev.last_pdf;
    if (!lastEv) return null;
    const total = (ev.view_count || 0) + (ev.pdf_count || 0);
    return (
        <span
            data-testid="invoice-read-receipt"
            title={`${ev.view_count || 0} view${ev.view_count === 1 ? "" : "s"} · ${ev.pdf_count || 0} PDF download${ev.pdf_count === 1 ? "" : "s"} · last activity ${relTime(lastEv)}`}
            className="ml-2 inline-flex items-center gap-1 text-[9px] text-emerald-400 uppercase tracking-wider"
        >
            <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
            Read · {relTime(lastEv)}{total > 1 ? ` · ${total}` : ""}
        </span>
    );
}

export default function Invoices() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [cases, setCases] = useState([]);
    const [open, setOpen] = useState(false);
    const [payFor, setPayFor] = useState(null);
    const [linkFor, setLinkFor] = useState(null);    // {invoice, url, sent_via}
    const [sendingLink, setSendingLink] = useState(false);
    const [linkChannels, setLinkChannels] = useState({ whatsapp: true, email: true, sms: false });
    const [linkCopied, setLinkCopied] = useState(false);
    const [shareFor, setShareFor] = useState(null);
    const [shareEvents, setShareEvents] = useState({});  // invoice_id → {view_count, pdf_count, last_view, last_pdf}
    const [recon, setRecon] = useState(null);     // {invoice, payments[], refunds[]}
    const [pay, setPay] = useState({ amount: 0, method: "upi", reference: "" });
    const [form, setForm] = useState({ client_id: "", case_id: "", items: [{ description: "", amount: 0 }], due_date: "", notes: "" });
    const [saving, setSaving] = useState(false);
    const [edit, setEdit] = useState(null);        // edit-invoice form state
    const [editSaving, setEditSaving] = useState(false);
    const [historyFor, setHistoryFor] = useState(null);
    const [emailFor, setEmailFor] = useState(null);      // {invoice, settings} — open the Email modal
    const [emailHistory, setEmailHistory] = useState([]);
    const [emailSending, setEmailSending] = useState(false);
    const [params] = useSearchParams();
    const statusFilter = params.get("status");    // unpaid | outstanding | overdue | paid
    const openParam = params.get("open");          // invoice id for deep-link auto-open

    const visibleItems = useMemo(() => {
        if (!statusFilter) return items;
        const today = istDateKey(new Date());
        if (statusFilter === "unpaid" || statusFilter === "outstanding") {
            return items.filter(i => ["sent", "overdue", "unpaid", "partial"].includes(i.status));
        }
        if (statusFilter === "overdue") {
            return items.filter(i => i.status === "overdue" || (i.status !== "paid" && i.due_date && i.due_date < today));
        }
        if (statusFilter === "paid") return items.filter(i => i.status === "paid");
        return items;
    }, [items, statusFilter]);
    const tableCtrl = useTableControls(visibleItems, {
        storageKey: "invoices",
        accessors: { total: (i) => i.amount, invoice_number: (i) => i.number },
        initialSort: { key: "due_date", dir: "desc" },
    });
    const vSorted = tableCtrl.rows;
    const { getRowProps: getInvRowProps } = useRowKeyboardNav({ items: vSorted, onOpen: (i) => openEdit(i), disabled: !!edit || open });

    useEffect(() => {
        if (!openParam || edit) return;
        const inv = items.find(x => x.id === openParam);
        if (inv) openEdit(inv);
    }, [openParam, items]);    // eslint-disable-line react-hooks/exhaustive-deps
    const filterLabel =
        statusFilter === "unpaid" || statusFilter === "outstanding" ? "Outstanding only" :
        statusFilter === "overdue" ? "Overdue only" :
        statusFilter === "paid" ? "Paid only" : null;
    const filterTone = statusFilter === "overdue" ? "danger" : statusFilter === "paid" ? "emerald" : "warning";

    const downloadPdf = async (inv) => {
        try {
            const resp = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Invoice-${inv.number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${a.download}`);
        } catch (e) { toast.error(apiError(e)); }
    };

    const openEmail = async (inv) => {
        try {
            const [s, h] = await Promise.all([
                api.get("/tenant/invoice-email-settings").catch(() => ({ data: {} })),
                api.get(`/invoices/${inv.id}/email-history`).catch(() => ({ data: [] })),
            ]);
            setEmailHistory(h.data || []);
            const client = clients.find(c => c.id === inv.client_id);
            setEmailFor({ invoice: inv, to: client?.email || "", subject: "", body: "", settings: s.data || {} });
        } catch (e) { toast.error(apiError(e)); }
    };
    const sendEmail = async () => {
        if (!emailFor) return;
        setEmailSending(true);
        try {
            const payload = {};
            if (emailFor.to?.trim()) payload.to = emailFor.to.trim();
            if (emailFor.subject?.trim()) payload.subject = emailFor.subject;
            if (emailFor.body?.trim()) payload.body = emailFor.body;
            const r = await api.post(`/invoices/${emailFor.invoice.id}/email`, payload);
            toast.success(`Sent to ${r.data.to}`);
            const h = await api.get(`/invoices/${emailFor.invoice.id}/email-history`);
            setEmailHistory(h.data || []);
            setEmailFor({...emailFor, subject: "", body: ""});
        } catch (e) { toast.error(apiError(e)); }
        finally { setEmailSending(false); }
    };

    const load = async () => {
        try {
            const [i, c, cs, ev] = await Promise.all([
                api.get("/invoices"),
                api.get("/clients"),
                api.get("/cases"),
                api.get("/invoices/share-events/bulk").catch(() => ({ data: {} })),
            ]);
            setItems(i.data); setClients(c.data); setCases(cs.data);
            setShareEvents(ev.data || {});
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const openRecon = async (inv) => {
        try {
            const { data } = await api.get(`/invoices/${inv.id}/payments`);
            setRecon({ invoice: inv, ...data });
        } catch (e) { toast.error(apiError(e)); }
    };

    const refund = async (p) => {
        const amt = window.prompt(`Refund amount (max ₹${p.amount}):`, p.amount);
        if (!amt) return;
        const reason = window.prompt("Reason for refund (optional):") || "";
        try {
            await api.post("/refunds", { payment_id: p.id, amount: Number(amt), reason });
            toast.success("Refund recorded");
            openRecon(recon.invoice);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const openLink = (inv) => {
        // Reset state and open dialog for that invoice
        setLinkFor({ invoice: inv, url: inv.payment_link_url || null, sent_via: null });
        setLinkChannels({ whatsapp: true, email: true, sms: false });
        setLinkCopied(false);
    };

    const sendLink = async () => {
        if (!linkFor) return;
        const channels = Object.entries(linkChannels).filter(([, v]) => v).map(([k]) => k);
        if (channels.length === 0) { toast.error("Pick at least one channel"); return; }
        setSendingLink(true);
        try {
            const { data } = await api.post(`/invoices/${linkFor.invoice.id}/payment-link`, { channels });
            setLinkFor({ invoice: linkFor.invoice, url: data.url, sent_via: data.sent_via });
            const sentChannels = data.sent_via.filter(s => s.status === "sent").map(s => s.channel);
            const mockedChannels = data.sent_via.filter(s => s.status === "mocked").map(s => s.channel);
            if (sentChannels.length > 0) toast.success(`Sent over ${sentChannels.join(", ")}`);
            else if (mockedChannels.length > 0) toast(`Link created. ${mockedChannels.join(", ")} provider not configured — share the link manually.`, { duration: 6000 });
            else toast.success("Payment link created");
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSendingLink(false); }
    };

    const copyLink = () => {
        if (!linkFor?.url) return;
        navigator.clipboard.writeText(linkFor.url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 1500);
    };

    const updItem = (idx, k, v) => {
        const items = [...form.items]; items[idx] = { ...items[idx], [k]: k === "amount" ? Number(v) || 0 : v };
        setForm({ ...form, items });
    };

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await api.post("/invoices", { ...form, items: form.items.filter(i => i.description) });
            toast.success("Invoice raised");
            setOpen(false); load();
            setForm({ client_id: "", case_id: "", items: [{ description: "", amount: 0 }], due_date: "", notes: "" });
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const recordPayment = async (e) => {
        e.preventDefault();
        try {
            await api.post("/payments", { invoice_id: payFor.id, amount: Number(pay.amount), method: pay.method, reference: pay.reference });
            toast.success("Payment recorded");
            setPayFor(null); load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const openEdit = (inv) => setEdit({
        id: inv.id,
        number: inv.number,
        due_date: inv.due_date || "",
        notes: inv.notes || "",
        status: inv.status || "unpaid",
        items: (inv.items || []).map(it => ({ description: it.description || "", amount: Number(it.amount) || 0 })),
        paid_amount: Number(inv.paid_amount || 0),
        reason: "",
        _orig: inv,
    });

    const updateEditItem = (idx, field, value) => {
        setEdit(prev => {
            const items = [...prev.items];
            items[idx] = { ...items[idx], [field]: field === "amount" ? value : value };
            return { ...prev, items };
        });
    };
    const addEditItem = () => setEdit(prev => ({ ...prev, items: [...prev.items, { description: "", amount: 0 }] }));
    const removeEditItem = (idx) => setEdit(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!edit.reason || edit.reason.trim().length < 3) {
            toast.error("Remarks are mandatory (minimum 3 characters)"); return;
        }
        // Validate items
        const cleaned = edit.items.filter(it => (it.description || "").trim() || Number(it.amount));
        if (cleaned.length === 0) { toast.error("Invoice must have at least one line item"); return; }
        const newTotal = cleaned.reduce((s, it) => s + (Number(it.amount) || 0), 0);
        if (newTotal < edit.paid_amount) {
            toast.error(`New total ₹${newTotal.toFixed(2)} cannot be less than amount already collected ₹${edit.paid_amount.toFixed(2)}`);
            return;
        }
        const payload = {};
        if ((edit.due_date || "") !== (edit._orig.due_date || "")) payload.due_date = edit.due_date;
        if ((edit.notes || "") !== (edit._orig.notes || "")) payload.notes = edit.notes;
        if ((edit.status || "") !== (edit._orig.status || "")) payload.status = edit.status;
        const origItems = (edit._orig.items || []).map(it => ({ description: it.description || "", amount: Number(it.amount) || 0 }));
        const cleanedNorm = cleaned.map(it => ({ description: it.description || "", amount: Number(it.amount) || 0 }));
        if (JSON.stringify(origItems) !== JSON.stringify(cleanedNorm)) payload.items = cleanedNorm;
        if (Object.keys(payload).length === 0) { toast.error("No fields changed"); return; }
        setEditSaving(true);
        try {
            await api.patch(`/invoices/${edit.id}`, payload, { headers: { "X-Change-Reason": edit.reason.trim() } });
            toast.success("Invoice updated · logged to audit trail");
            setEdit(null);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setEditSaving(false); }
    };

    const editTotal = (edit?.items || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);

    const total = form.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

    return (
        <>
            <PageHeader
                title="Invoices &amp; payments"
                subtitle="Raise. Collect. Reconcile. With AutoPay-ready Razorpay rails."
                action={<GoldButton onClick={() => setOpen(true)} data-testid="invoices-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>New invoice</GoldButton>}
            />

            {filterLabel && (
                <div className="mb-4 flex items-center gap-2" data-testid="invoices-filter-banner">
                    <FilterPill label={filterLabel} testId={`invoices-pill-${statusFilter}`} tone={filterTone}/>
                    <span className="text-xs text-muted-foreground">{visibleItems.length} invoice{visibleItems.length === 1 ? "" : "s"}</span>
                </div>
            )}

            {visibleItems.length === 0 ? (
                <EmptyState title={filterLabel ? "No matching invoices" : "No invoices"} subtitle={filterLabel ? "Try clearing the filter to see all invoices." : "Bill your first client."} action={<GoldButton onClick={() => setOpen(true)} data-testid="invoices-empty-add">Raise invoice</GoldButton>}/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[640px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh ctrl={tableCtrl} field="invoice_number" testIdPrefix="sort-inv">Number</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="client_name" testIdPrefix="sort-inv">Client</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="total" testIdPrefix="sort-inv">Amount</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="paid_amount" testIdPrefix="sort-inv">Paid</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="status" testIdPrefix="sort-inv">Status</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="due_date" testIdPrefix="sort-inv">Due</SortableTh>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {vSorted.map((i, idx) => (
                                <tr key={i.id} {...getInvRowProps(idx)} className="border-b border-border hover:bg-muted/50 cursor-pointer" data-testid={`invoice-row-${i.id}`} onClick={(e) => { if ((e.target.closest('button,a'))) return; openEdit(i); }}>
                                    <td className="px-4 py-3 font-mono text-xs text-primary">
                                        {i.number}
                                        <ReadReceipt ev={shareEvents[i.id]}/>
                                    </td>
                                    <td className="px-4 py-3">{i.client_name}</td>
                                    <td className="px-4 py-3 font-mono">₹{i.amount.toLocaleString()}</td>
                                    <td className="px-4 py-3 font-mono text-muted-foreground">₹{(i.paid_amount || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3"><StatusBadge status={i.status}/>
                                        {(i.acc_adjustment_entry_ids?.length || i.acc_payment_entry_ids?.length || i.acc_issue_entry_id) && (
                                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400"
                                                 title={[
                                                     i.acc_issue_entry_id        ? `Issued: ${i.acc_issue_entry_id}` : "",
                                                     ...(i.acc_adjustment_entry_ids || []).map(e => `Adjusted: ${e}`),
                                                     ...(i.acc_payment_entry_ids   || []).map(e => `Paid: ${e}`),
                                                 ].filter(Boolean).join("\n")}
                                                 data-testid={`invoice-synced-${i.id}`}>
                                                <CheckCircle2 className="w-3 h-3" strokeWidth={2}/>
                                                <span>Synced to Books</span>
                                                {((i.acc_adjustment_entry_ids?.length || 0) + (i.acc_payment_entry_ids?.length || 0)) > 0 && (
                                                    <span className="font-mono">({1 + (i.acc_adjustment_entry_ids?.length || 0) + (i.acc_payment_entry_ids?.length || 0)})</span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{i.due_date || "—"}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => setShareFor(i)} data-testid={`invoice-share-${i.id}`} className="text-xs text-primary hover:underline mr-3 inline-flex items-center gap-1"><Share2 className="w-3 h-3" strokeWidth={1.5}/>Share</button>
                                        <button onClick={() => downloadPdf(i)} data-testid={`invoice-pdf-${i.id}`} title="Download branded PDF (uses Settings → Invoice branding)" className="text-xs text-primary hover:underline mr-3 inline-flex items-center gap-1"><Download className="w-3 h-3" strokeWidth={1.5}/>PDF</button>
                                        <button onClick={() => openEmail(i)} data-testid={`invoice-email-${i.id}`} className="text-xs text-primary hover:underline mr-3 inline-flex items-center gap-1"><Mail className="w-3 h-3" strokeWidth={1.5}/>Email</button>
                                        <button onClick={() => setHistoryFor(i)} data-testid={`invoice-history-${i.id}`} className="text-xs text-muted-foreground hover:text-primary mr-3 inline-flex items-center gap-1"><History className="w-3 h-3" strokeWidth={1.5}/>History</button>
                                        <button onClick={() => openEdit(i)} data-testid={`invoice-edit-${i.id}`} className="text-xs text-muted-foreground hover:text-primary mr-3 inline-flex items-center gap-1"><Pencil className="w-3 h-3" strokeWidth={1.5}/>Edit</button>
                                        <button onClick={() => openRecon(i)} data-testid={`invoice-recon-${i.id}`} className="text-xs text-muted-foreground hover:text-primary mr-3 inline-flex items-center gap-1"><ReceiptIcon className="w-3 h-3" strokeWidth={1.5}/>Reconcile</button>
                                        {i.status !== "paid" && (
                                            <>
                                                <button onClick={() => openLink(i)} data-testid={`invoice-link-${i.id}`}
                                                    className="text-xs text-primary hover:underline mr-3 inline-flex items-center gap-1"><Send className="w-3 h-3" strokeWidth={1.5}/>Send link</button>
                                                <button onClick={() => { setPayFor(i); setPay({ amount: i.amount - (i.paid_amount || 0), method: "upi", reference: "" }); }} data-testid={`invoice-pay-${i.id}`}
                                                    className="text-xs text-primary hover:underline">Record payment</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="New invoice" size="lg" testId="invoice-modal">
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Client"><Select required value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} data-testid="invoice-client-select"
                            options={[{ value: "", label: "— select —" }, ...clients.map(c => ({ value: c.id, label: c.name }))]}/></Field>
                        <Field label="Case (optional)"><Select value={form.case_id} onChange={e => setForm({...form, case_id: e.target.value})}
                            options={[{ value: "", label: "—" }, ...cases.map(c => ({ value: c.id, label: caseLabel(c) }))]}/></Field>
                    </div>
                    <div>
                        <div className="overline mb-2">Line items</div>
                        <div className="space-y-2">
                            {form.items.map((it, i) => (
                                <div key={`item-${i}-${it.description || ""}`} className="grid grid-cols-[1fr_140px_40px] gap-2">
                                    <Input placeholder="Description" value={it.description} onChange={e => updItem(i, "description", e.target.value)} data-testid={`invoice-item-desc-${i}`}/>
                                    <Input type="number" placeholder="Amount" value={it.amount} onChange={e => updItem(i, "amount", e.target.value)} data-testid={`invoice-item-amount-${i}`}/>
                                    <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, k) => k !== i) })} className="text-muted-foreground hover:text-red-400">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { description: "", amount: 0 }] })} data-testid="invoice-add-item-btn"
                            className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"><PlusIcon className="w-3 h-3"/> Add line</button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Due date"><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}/></Field>
                        <div className="flex items-end">
                            <div className="ml-auto">
                                <div className="overline">Total</div>
                                <div className="font-serif text-3xl">₹{total.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                    <Field label="Notes"><Textarea rows="2" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></Field>
                    <div className="flex justify-end pt-2"><GoldButton disabled={saving} data-testid="invoice-save-btn">{saving ? "Saving…" : "Raise invoice"}</GoldButton></div>
                </form>
            </Modal>

            <Modal open={!!payFor} onClose={() => setPayFor(null)} title={`Record payment — ${payFor?.number || ""}`} testId="payment-modal">
                {payFor && (
                    <form onSubmit={recordPayment} className="space-y-4">
                        <div className="text-sm text-muted-foreground">Outstanding: ₹{(payFor.amount - (payFor.paid_amount || 0)).toLocaleString()}</div>
                        <Field label="Amount"><Input required type="number" value={pay.amount} onChange={e => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input"/></Field>
                        <Field label="Method">
                            <Select value={pay.method} onChange={e => setPay({ ...pay, method: e.target.value })} options={["cash", "upi", "bank", "cheque", "razorpay"]}/>
                        </Field>
                        <Field label="Reference (optional)"><Input value={pay.reference} onChange={e => setPay({ ...pay, reference: e.target.value })}/></Field>
                        <div className="flex justify-end pt-2"><GoldButton data-testid="payment-save-btn">Record</GoldButton></div>
                        <p className="text-xs text-muted-foreground">Razorpay is wired but the live gateway requires your keys — mock recorded for now.</p>
                    </form>
                )}
            </Modal>

            <Modal open={!!linkFor} onClose={() => setLinkFor(null)} title={`Send payment link — ${linkFor?.invoice?.number || ""}`} testId="payment-link-modal">
                {linkFor && (
                    <div className="space-y-5">
                        <div className="text-sm text-muted-foreground">
                            Outstanding: <span className="text-foreground font-mono">₹{(linkFor.invoice.amount - (linkFor.invoice.paid_amount || 0)).toLocaleString()}</span> · Client: <span className="text-foreground">{linkFor.invoice.client_name}</span>
                        </div>
                        {linkFor.url ? (
                            <div className="bg-background border border-border p-4">
                                <div className="overline mb-2 text-[10px]">Payment link</div>
                                <div className="flex items-center gap-2">
                                    <a href={linkFor.url} target="_blank" rel="noreferrer" className="flex-1 text-xs font-mono text-primary hover:underline truncate" data-testid="payment-link-url">{linkFor.url}</a>
                                    <button onClick={copyLink} data-testid="payment-link-copy" className="px-3 py-2 border border-border hover:border-primary text-xs inline-flex items-center gap-1">
                                        {linkCopied ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" strokeWidth={2}/>Copied</> : <><Copy className="w-3 h-3" strokeWidth={1.5}/>Copy</>}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-xs text-muted-foreground py-3 border border-dashed border-border px-4">
                                We&apos;ll create a Razorpay payment link and ping your client on the channels below.
                            </div>
                        )}
                        <div>
                            <div className="overline mb-2">Send via</div>
                            <div className="flex flex-wrap gap-3">
                                {["whatsapp", "email", "sms"].map(ch => (
                                    <label key={ch} className="flex items-center gap-2 text-sm border border-border px-3 py-2 cursor-pointer hover:border-primary">
                                        <input type="checkbox" className="w-4 h-4 accent-primary" checked={linkChannels[ch]} onChange={e => setLinkChannels({...linkChannels, [ch]: e.target.checked})} data-testid={`payment-link-ch-${ch}`}/>
                                        <span className="capitalize">{ch}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-2">If a channel&apos;s provider isn&apos;t configured (Super Admin → Integrations), the message will be logged with status <span className="font-mono">mocked</span> but not delivered.</p>
                        </div>
                        {linkFor.sent_via && (
                            <div className="text-xs space-y-1" data-testid="payment-link-results">
                                {linkFor.sent_via.map((s) => (
                                    <div key={s.channel} className={`flex items-center justify-between border border-border px-3 py-2 ${s.status === "sent" ? "border-emerald-700/40" : s.status === "mocked" ? "border-primary/40" : "border-red-700/40"}`}>
                                        <span className="capitalize">{s.channel}</span>
                                        <span className="font-mono uppercase tracking-[0.2em] text-[10px]">
                                            {s.status === "mocked" ? "provider not set" : s.status}
                                            {s.error && ` — ${String(s.error).slice(0,60)}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex justify-end pt-2">
                            <GoldButton onClick={sendLink} disabled={sendingLink} data-testid="payment-link-send-btn">
                                {sendingLink ? "Working…" : (linkFor.url ? "Resend" : "Create & send")}
                            </GoldButton>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal open={!!recon} onClose={() => setRecon(null)} title={`Reconciliation — ${recon?.invoice?.number || ""}`} size="lg" testId="reconciliation-modal">
                {recon && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                            <div className="bg-background border border-border p-4">
                                <div className="overline mb-2">Billed</div>
                                <div className="font-serif text-2xl">₹{recon.invoice.amount.toLocaleString()}</div>
                            </div>
                            <div className="bg-background border border-border p-4">
                                <div className="overline mb-2">Collected</div>
                                <div className="font-serif text-2xl text-primary">₹{(recon.invoice.paid_amount || 0).toLocaleString()}</div>
                            </div>
                            <div className="bg-background border border-border p-4">
                                <div className="overline mb-2">Outstanding</div>
                                <div className="font-serif text-2xl">₹{(recon.invoice.amount - (recon.invoice.paid_amount || 0)).toLocaleString()}</div>
                            </div>
                        </div>
                        <div>
                            <div className="overline mb-3">Payments</div>
                            {recon.payments.length === 0 ? <div className="text-sm text-muted-foreground">No payments.</div> : (
                                <div className="border border-border divide-y divide-border">
                                    {recon.payments.map(p => (
                                        <div key={p.id} className="px-4 py-3 flex items-center justify-between text-sm flex-wrap gap-2" data-testid={`payment-${p.id}`}>
                                            <div className="min-w-0">
                                                <div className="font-mono text-xs text-muted-foreground">{fmtDateTime(p.created_at)}</div>
                                                <div className="mt-1">₹{p.amount.toLocaleString()} · <span className="text-muted-foreground">{p.method}</span> {p.reference && <span className="text-muted-foreground font-mono text-xs">({p.reference})</span>}</div>
                                                {p.initiated_by_client && <div className="text-[10px] uppercase tracking-[0.2em] text-primary mt-1">By client</div>}
                                            </div>
                                            <button onClick={() => refund(p)} data-testid={`refund-${p.id}`} className="text-xs border border-border hover:border-red-400 hover:text-red-400 px-3 py-1 transition-colors inline-flex items-center gap-1"><Undo2 className="w-3 h-3" strokeWidth={1.5}/>Refund</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {recon.refunds.length > 0 && (
                            <div>
                                <div className="overline mb-3">Refunds</div>
                                <div className="border border-border divide-y divide-border">
                                    {recon.refunds.map(r => (
                                        <div key={r.id} className="px-4 py-3 text-sm">
                                            <div className="font-mono text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</div>
                                            <div className="mt-1">−₹{r.amount.toLocaleString()} · <span className="text-muted-foreground">{r.status}</span></div>
                                            {r.reason && <div className="text-xs text-muted-foreground mt-1">{r.reason}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
            <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit invoice — ${edit?.number || ""}`} size="lg" testId="invoice-edit-modal">
                {edit && (
                    <form onSubmit={saveEdit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Due date">
                                <Input type="date" value={edit.due_date} onChange={e => setEdit({ ...edit, due_date: e.target.value })} data-testid="invoice-edit-due-date"/>
                            </Field>
                            <Field label="Status">
                                <Select value={edit.status} onChange={e => setEdit({ ...edit, status: e.target.value })} data-testid="invoice-edit-status"
                                    options={["unpaid", "partial", "paid", "void", "written_off"]}/>
                            </Field>
                        </div>

                        <div>
                            <div className="overline mb-2">Line items</div>
                            <div className="space-y-2">
                                {edit.items.map((it, i) => (
                                    <div key={`edit-item-${i}-${it.description || ""}`} className="grid grid-cols-12 gap-2 items-center">
                                        <Input className="col-span-7" placeholder="Description" value={it.description} onChange={e => updateEditItem(i, "description", e.target.value)} data-testid={`invoice-edit-item-desc-${i}`}/>
                                        <Input className="col-span-4" type="number" min="0" step="0.01" placeholder="Amount" value={it.amount} onChange={e => updateEditItem(i, "amount", e.target.value === "" ? "" : Number(e.target.value))} data-testid={`invoice-edit-item-amount-${i}`}/>
                                        <button type="button" onClick={() => removeEditItem(i)} disabled={edit.items.length === 1} data-testid={`invoice-edit-item-remove-${i}`} className="col-span-1 text-muted-foreground hover:text-red-400 disabled:opacity-30 text-xs"><Trash2 className="w-4 h-4 mx-auto" strokeWidth={1.5}/></button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addEditItem} data-testid="invoice-edit-item-add" className="text-xs text-primary mt-2 inline-flex items-center gap-1">
                                <PlusIcon className="w-3.5 h-3.5" strokeWidth={1.5}/> Add line item
                            </button>
                            <div className="text-right text-sm mt-3 font-mono">
                                <span className="text-muted-foreground">New total: </span>
                                <span className="text-primary text-lg" data-testid="invoice-edit-total">₹{editTotal.toFixed(2)}</span>
                                {edit.paid_amount > 0 && (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">Already collected ₹{edit.paid_amount.toFixed(2)} — new total cannot go below this.</div>
                                )}
                            </div>
                        </div>

                        <Field label="Notes">
                            <Textarea rows="2" value={edit.notes} onChange={e => setEdit({ ...edit, notes: e.target.value })}/>
                        </Field>
                        <Field label="Remarks (mandatory · logged to audit trail)" hint="Explain why this edit is being made. Minimum 3 characters.">
                            <Textarea rows="2" value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} data-testid="invoice-edit-reason" placeholder="e.g. Due date extended at client's request"/>
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEdit(null)} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
                            <GoldButton type="submit" disabled={editSaving} data-testid="invoice-edit-save-btn">{editSaving ? "Saving…" : "Save changes"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>

            <HistoryDrawer open={!!historyFor} onClose={() => setHistoryFor(null)} entity="invoice" entityId={historyFor?.id} title={`${historyFor?.number || ""} · ${historyFor?.client_name || ""}`} />
            <InvoiceShareModal scope="tenant" invoice={shareFor} open={!!shareFor} onClose={() => setShareFor(null)} />

            <Modal open={!!emailFor} onClose={() => setEmailFor(null)} title={`Email invoice — ${emailFor?.invoice?.number || ""}`} size="lg" testId="invoice-email-modal">
                {emailFor && (
                    <div className="space-y-4">
                        <div className="border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
                            <div className="flex items-start gap-2">
                                <Mail className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0"/>
                                <div>
                                    PDF invoice will be attached automatically.
                                    {emailFor.settings?.cc_firm_owner && <> Firm owner will be CC'd.</>}
                                    {emailFor.settings?.attach_payment_link && <> A payment link will be included in the body.</>}
                                    {" "}Subject and body use <code className="text-primary">{`{{tokens}}`}</code> — leave blank to use your default template.
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">To (recipient)</label>
                            <input className="w-full mt-1 bg-card border border-border px-3 py-2 text-sm"
                                value={emailFor.to} onChange={e => setEmailFor({...emailFor, to: e.target.value})}
                                placeholder="client@example.com" data-testid="email-to"/>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Subject override (optional)</label>
                            <input className="w-full mt-1 bg-card border border-border px-3 py-2 text-sm"
                                value={emailFor.subject} onChange={e => setEmailFor({...emailFor, subject: e.target.value})}
                                placeholder={emailFor.settings?.subject_template || "Use default subject template"}
                                data-testid="email-subject"/>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Body override (optional)</label>
                            <textarea rows={5} className="w-full mt-1 bg-card border border-border px-3 py-2 text-sm font-mono"
                                value={emailFor.body} onChange={e => setEmailFor({...emailFor, body: e.target.value})}
                                placeholder={emailFor.settings?.body_template || "Use default body template"}
                                data-testid="email-body"/>
                        </div>

                        {emailHistory.length > 0 && (
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Past sends ({emailHistory.length})</div>
                                <div className="max-h-40 overflow-y-auto border border-border">
                                    <table className="w-full text-[11px]">
                                        <tbody>{emailHistory.map(h => (
                                            <tr key={h.id} className="border-b border-border" data-testid={`email-history-${h.id}`}>
                                                <td className="px-2 py-1.5 text-muted-foreground">{new Date(h.sent_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</td>
                                                <td className="px-2 py-1.5 truncate max-w-[180px]">{h.to}</td>
                                                <td className="px-2 py-1.5">
                                                    <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 ${
                                                        h.status === "sent" ? "bg-emerald-950/40 text-emerald-300" : "bg-rose-950/40 text-rose-300"
                                                    }`}>{h.status}</span>
                                                </td>
                                                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                                                    {h.trigger}{h.has_pdf && " · PDF"}{h.payment_link_attached && " · pay-link"}
                                                </td>
                                            </tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-3 border-t border-border">
                            <a href="/app/settings/invoice-email" className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground hover:text-primary" data-testid="invoice-email-settings-link">Configure auto-send & defaults →</a>
                            <div className="flex gap-2">
                                <button onClick={() => setEmailFor(null)} className="px-4 py-2 border border-border text-muted-foreground hover:text-foreground text-xs uppercase tracking-[0.1em]">Close</button>
                                <button onClick={sendEmail} disabled={emailSending || !emailFor.to?.trim()}
                                    className="px-4 py-2 bg-primary text-black hover:bg-amber-300 disabled:opacity-50 text-xs uppercase tracking-[0.1em]"
                                    data-testid="invoice-email-send">
                                    {emailSending ? "Sending…" : (emailHistory.length > 0 ? "Resend" : "Send")} → Client
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}